import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

const STORAGE_KEY = 'aiads.tour.state';

interface StoredState {
  active: boolean;
  step: number;
}

function readState(): StoredState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { active: false, step: 0 };
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    return { active: !!parsed.active, step: Math.max(0, parsed.step ?? 0) };
  } catch {
    return { active: false, step: 0 };
  }
}

function writeState(state: StoredState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota errors are non-fatal
  }
}

/**
 * pwa_ — LocalStorage-backed product tour controller. Exposes imperative
 * navigation and a `complete()` that POSTs to /user/tour-complete. The list
 * of steps is passed at mount time by ProductTour, so this hook only knows
 * about the cursor.
 */
export interface UseTourResult {
  active: boolean;
  currentStep: number;
  start: () => void;
  next: (max: number) => void;
  prev: () => void;
  skip: () => void;
  complete: () => Promise<void>;
}

export function useTour(): UseTourResult {
  const [state, setState] = useState<StoredState>(() => readState());

  useEffect(() => {
    writeState(state);
  }, [state]);

  const start = useCallback(() => {
    setState({ active: true, step: 0 });
  }, []);

  const next = useCallback((max: number) => {
    setState((prev) => ({ active: prev.active, step: Math.min(prev.step + 1, max - 1) }));
  }, []);

  const prev = useCallback(() => {
    setState((prev) => ({ active: prev.active, step: Math.max(prev.step - 1, 0) }));
  }, []);

  const skip = useCallback(() => {
    setState({ active: false, step: 0 });
  }, []);

  const complete = useCallback(async () => {
    try {
      await api.post('/user/tour-complete');
    } catch (err) {
      console.error(err);
    } finally {
      setState({ active: false, step: 0 });
    }
  }, []);

  return {
    active: state.active,
    currentStep: state.step,
    start,
    next,
    prev,
    skip,
    complete,
  };
}
